import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ChevronRight,
  Package,
  Star,
  Truck,
  Shield,
  Users,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const WebsiteHome = () => {
  const [storeInfo, setStoreInfo] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [infoRes, productsRes, newRes, promosRes, statsRes] = await Promise.all([
          axios.get(`${API}/api/website/info`),
          axios.get(`${API}/api/website/featured-products?limit=8`),
          axios.get(`${API}/api/website/new-arrivals?limit=8&days=14`),
          axios.get(`${API}/api/website/promotions`),
          axios.get(`${API}/api/website/stats`),
        ]);
        setStoreInfo(infoRes.data);
        setFeaturedProducts(productsRes.data.products || []);
        setNewArrivals(newRes.data.products || []);
        setPromotions(promosRes.data.promotions || []);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 text-center md:text-left">
              <div className="mb-6">
                <img 
                  src="/logo.jpg" 
                  alt="BOSVELD BOU & HARDEWARE" 
                  className="h-24 md:h-32 mx-auto md:mx-0 rounded-lg shadow-2xl bg-white p-2"
                />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                Your Trusted
                <span className="text-blue-400 block">Hardware Partner</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-xl">
                Quality building materials, tools, and hardware supplies for all your construction and DIY needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Link to="/site/products">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
                    Browse Products
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/site/contact">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f9fafb"/>
          </svg>
        </div>
      </section>

      {/* New Arrivals Section */}
      {newArrivals.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 text-white p-2 rounded-lg">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800">New Arrivals</h2>
                  <p className="text-sm text-gray-500">Just added to our shelves</p>
                </div>
              </div>
              <Link to="/site/products">
                <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50">
                  View All
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {newArrivals.slice(0, 8).map((product) => (
                <Card key={product.id} className="group hover:shadow-lg transition-all duration-300 overflow-hidden border-0 bg-white relative">
                  <div className="absolute top-2 left-2 z-10">
                    <Badge className="bg-amber-500 text-white text-xs font-bold px-2 py-1 shadow-md">
                      NEW
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <div className="aspect-square bg-gray-200 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <Package className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{product.department}</p>
                    <h3 className="font-medium text-sm line-clamp-2 mb-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-bold text-amber-600">
                        {formatPrice(product.selling_price_incl_vat)}
                      </p>
                      <Badge 
                        variant="outline" 
                        className={
                          product.stock_status === "In Stock" 
                            ? "bg-green-50 text-green-700 border-green-200" 
                            : "bg-red-50 text-red-700 border-red-200"
                        }
                      >
                        {product.stock_status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800">Featured Products</h2>
              <Link to="/site/products">
                <Button variant="outline" className="text-blue-600 border-blue-600 hover:bg-blue-50">
                  View All
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {featuredProducts.slice(0, 8).map((product) => (
                <Card key={product.id} className="group hover:shadow-lg transition-all duration-300 overflow-hidden border-0 bg-gray-50">
                  <CardContent className="p-4">
                    <div className="aspect-square bg-gray-200 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <Package className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{product.department}</p>
                    <h3 className="font-medium text-sm line-clamp-2 mb-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-bold text-blue-600">
                        {formatPrice(product.selling_price_incl_vat)}
                      </p>
                      <Badge 
                        variant="outline" 
                        className={
                          product.stock_status === "In Stock" 
                            ? "bg-green-50 text-green-700 border-green-200" 
                            : "bg-red-50 text-red-700 border-red-200"
                        }
                      >
                        {product.stock_status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Current Promotions */}
      {promotions.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold">Current Specials</h2>
              <Link to="/site/promotions">
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  View All Specials
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {promotions.slice(0, 3).map((promo, i) => (
                <Card key={i} className="bg-white/10 backdrop-blur border-white/20 text-white">
                  <CardContent className="p-6">
                    <Badge className="bg-yellow-500 text-black mb-3">SPECIAL</Badge>
                    <h3 className="text-xl font-bold mb-2">{promo.name}</h3>
                    <p className="text-gray-200 text-sm mb-4">{promo.description}</p>
                    {promo.discount_percentage && (
                      <p className="text-3xl font-bold text-yellow-400">
                        {promo.discount_percentage}% OFF
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Info Bar */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <MapPin className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold">Visit Us</p>
                <p className="text-sm text-gray-400">{storeInfo?.address}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Phone className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold">Call Us</p>
                <p className="text-sm text-gray-400">{storeInfo?.phone}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold">Email Us</p>
                <p className="text-sm text-gray-400">{storeInfo?.email}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold">Store Hours</p>
                <p className="text-sm text-gray-400">{storeInfo?.hours?.weekdays}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-gray-400 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.jpg" alt="BBH" className="h-10 rounded bg-white p-1" />
              <span className="text-white font-semibold">BOSVELD BOU & HARDEWARE</span>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} BOSVELD BOU & HARDEWARE. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebsiteHome;
